<script setup lang="ts">
//#region imports
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import _ from '@/setup/lodash';
import { useTableFilter, useTablePaginator, useTableSort } from '@/composables/useTableLS';
import { useDetectPermissions } from '@/composables/useDetectPermissions';
import { useApi } from '@/api';
import { useLs } from '@/setup/vueLs';
import { usePopupManager } from '@/components/PopupManager.vue';
import { useUpdateUrl } from '@/composables/useUpdateUrl';
import UiPagination from '@/components/UIElements/UiPagination.vue';
import { useTypedStore } from '@/store';
import type { UiTableNewField, UiTableNewFilter, UiTableNewSortEmit } from '@/types/table';
import type {
  GetCpaConversionsResponse,
  GetCpaConversionsData,
  GetAffiliatesResponse,
  GetAffiliatesData,
  GetAffiliateOffersResponse,
  GetAffiliateOffersData,
} from '@/types/openapi-ts';
import type { ConversionSelectPayload } from './components/CpaConversionMoveToHoldPopup.vue';
import type { SettingsPageQuery } from '@/types/core';
//#endregion

//#region types
type CpaConversionsRow = GetCpaConversionsResponse['payload'][number];
type CpaConversionsPermissions = NonNullable<GetCpaConversionsResponse['misc']>['permissions'];
type CpaConversionsQuery = SettingsPageQuery<GetCpaConversionsData['query']>;

type AffiliateId = string | Array<string> | null;
//#endregion

//#region const
const LS_PATH = 'CpaConversions';

const sizes = {
  1080: 20,
  1440: 30,
};

const fields: UiTableNewField<CpaConversionsRow>[] = [
  {
    name: 'incrementing_id',
    align: 'left',
    sortable: 'custom',
    width: '80',
    class: 'text',
  },
  {
    name: 'site_name',
    width: '188',
    align: 'left',
    renderValue: (val, row) => {
      const img = row.site_favicon_base64 ? `<img src="${row.site_favicon_base64}" alt=""/>` : '';
      return `<div class="t:flex t:space-x-4 t:items-center">${img}<div>${row.site_name}</div></div>`;
    },
  },
  {
    name: 'site_player_id',
    minWidth: '124',
    class: 'text',
    link: getPlayerLink,
  },
  {
    name: 'affiliate_email',
    align: 'left',
    minWidth: '190',
    class: 'text',
    link: getAffiliateLink,
  },
  {
    name: 'affiliate_offer_name',
    minWidth: '140',
    class: 'text',
  },
  {
    name: 'status',
    width: '90',
    type: 'status',
    statusText: (value) => t(`payments.cpa_conversions.statuses.${value}`),
    status: (value) => value,
  },

  {
    name: 'revenue',
    headerAlign: 'right',
    align: 'right',
    width: '92',
    format: 'formatMoney',
    computedClass: getCurrencyClass,
  },
  {
    name: 'hold_expires_at',
    width: '120',
    sortable: 'custom',
    format: 'date-time',
    class: 'text',
  },
  {
    name: 'reviewed_by',
    minWidth: '160',
    link: getReviewedByLink,
    renderValue: (_, row) => row.reviewed_by_admin_name!,
  },
];
//#endregion

//#region composables
const api = useApi();
const ls = useLs();
const route = useRoute();
const store = useTypedStore();
const popupManager = usePopupManager();
const { checkRequest, checkRoute } = useDetectPermissions();
const updateUrl = useUpdateUrl();
const { t } = useI18n();

const { limit, count, page, setPage } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});
const { sortProp, sortOrder, optionsSort } = useTableSort({
  initialOptions:{
    sort_column: 'incrementing_id',
    sort_dir: 'desc',
  },
  lsPath: LS_PATH,
  fields,
});
const { filterParams, setFilter } = useTableFilter({
  lsPath: LS_PATH,
});
//#endregion

//#region state
const tableData = ref<GetCpaConversionsResponse['payload']>([]);
const isDataLoading = ref(false);
const isFirstLoading = ref(true);
const permissions = ref<CpaConversionsPermissions>([]);

const notApplyFilterState = ref<UiTableNewFilter['filter']>({});
const isSelectLoading = ref(false);
const affiliatesList = ref<GetAffiliatesResponse['payload']>([]);
const offersList = ref<GetAffiliateOffersResponse['payload']>([]);
//#endregion

//#region computed
const brands = computed(() => store.getters['misc/brands']);

const getBrandImgUrl = computed(() => {
  const brandFavicon = notApplyFilterState.value.site_id ? brands.value.filter((brand) => brand.id === notApplyFilterState.value.site_id)[0]!.favicon_base64 : '';
  return brandFavicon !== undefined ? brandFavicon : false;
});

const filterStatuses = computed(() => {
  return [
    {
      id: 'pending_qualification',
      name: t('payments.cpa_conversions.statuses.pending_qualification'),
    },
    {
      id: 'in_hold',
      name: t('payments.cpa_conversions.statuses.in_hold'),
    },
    {
      id: 'on_review',
      name: t('payments.cpa_conversions.statuses.on_review'),
    },
    {
      id: 'approved',
      name: t('payments.cpa_conversions.statuses.approved'),
    },
    {
      id: 'declined',
      name: t('payments.cpa_conversions.statuses.declined'),
    },
  ];
});

const filtersList = computed(() => {
  return [
    {
      id: 'site_id',
      name: 'brand',
      type: 'select',
      params: {
        list: brands.value,
        filterable: true,
        selectItemLogo: getBrandImgUrl.value,
        listItemLogo: 'favicon_base64',
      },
    },
    {
      id: 'affiliate_id',
      name: 'affiliate',
      type: 'select',
      permission: 'affiliates_view',
      params: {
        list: affiliatesList.value,
        optionLabelParam: 'email',
        optionNameParam: 'email',
        'collapse-tag-tooltip': true,
        'remote-method': getAffiliates,
        loading: isSelectLoading.value,
        remote: true,
      },
    },
    {
      id: 'affiliate_offer_id',
      name: 'offer',
      type: 'select',
      permission: 'affiliates_view',
      params: {
        list: offersList.value,
        optionLabelParam: 'full_name',
        optionNameParam: 'full_name',
        disabled: !notApplyFilterState.value.affiliate_id,
      },
    },
    {
      id: 'status',
      name: 'status',
      type: 'select',
      params: {
        list: filterStatuses.value,
        multiple: true,
        filterable: false,
      },
    },
  ];
});
//#endregion

//#region methods
function initFilter() {
  const affiliateId = filterParams.value.affiliate_id as string | string[];

  if (affiliateId && affiliateId.length > 0) {
    getAffiliates(undefined, filterParams.value.affiliate_id as AffiliateId);
  }
}

function initData() {
  if (checkRequest('payments_view')){
    const _urlData = updateUrl.getParseParams(route.hash);

    if (!_.isEmpty(_urlData)) {
      setPage(_urlData);
      setFilter(_urlData);

      delete _urlData.page;
      delete _urlData.limit;

      _.merge(optionsSort.value, _urlData);
    }

    nextTick(() => {
      fetchData();
    });
  }

  checkRoute('payments_view');
}

function _query() {
  return {
    ...optionsSort.value,
    limit: limit.value,
    offset: limit.value * page.value - limit.value,
  } as GetCpaConversionsData['query'];
}

function _completedFilterUrl() {
  const _dataFilters: CpaConversionsQuery =_.omit(_query(), 'offset');
  _dataFilters.page = page.value;
  _dataFilters.filters = JSON.stringify(filterParams.value);
  if(Object.keys(filterParams.value).length <= 0) {
    delete _dataFilters.filters;
  }
  updateUrl.updateGetParams(_dataFilters);
}

async function fetchData(silent = false) {
  if (!silent && !isFirstLoading.value) {
    isDataLoading.value = true;
  }

  _completedFilterUrl();
  let options = _query();
  options = {
    ...options,
    ...filterParams.value,
  };

  try {
    const { data: { payload, misc } } = await api.getCpaConversions(options);

    tableData.value = payload;
    count.value = misc!.count;
    permissions.value = misc!.permissions;
  } finally {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  }
}

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};

function handleRefresh () {
  page.value = 1;
  fetchData();
}

function getPlayerLink(row: CpaConversionsRow) {
  return `/players/${row.player_id}/info`;
}

function getAffiliateLink(row: CpaConversionsRow) {
  return `/affiliates/${row.affiliate_id}/info`;
}

function getReviewedByLink(row: CpaConversionsRow) {
  return `/affiliates/${row.reviewed_by_admin_id}/info`;
}

function getCurrencyClass(value: number) {
  return value < 0 ? 'negative' : '';
}

function handleView(row: CpaConversionsRow) {
  if (!row.current_state) return;

  popupManager.cpaConversionDetails.open({
    data: {
      conversion: row.current_state,
    },
  });
}

function canMoveToHold(row: CpaConversionsRow) {
  const canMakeManualCpaPayout = permissions.value[row.incrementing_id]?.make_manual_cpa_payouts;

  return row.status === 'pending_qualification'
    && Array.isArray(row.current_state)
    && row.current_state.length > 0
    && canMakeManualCpaPayout;
}

async function handleMoveToHold(row: CpaConversionsRow) {
  if (!canMoveToHold(row)) return;

  isDataLoading.value = true;

  try {
    const response = await api.getCpaConversionPayouts(row.incrementing_id);
    const { rule_cpa_payouts = [], data } = response.data.payload;

    if (!Array.isArray(rule_cpa_payouts) || !rule_cpa_payouts.length) {
      return;
    }

    await popupManager.cpaConversionMoveToHold.open({
      data: {
        data,
        conversionId: row.incrementing_id,
        conversionStates: rule_cpa_payouts,
      },
      listeners: {
        select: (payload) => {
          handleMoveToHoldConfirm(row, payload);
        },
      },
    });
  } finally {
    isDataLoading.value = false;
  }
}

function handleMoveToHoldConfirm(row: CpaConversionsRow, payload: ConversionSelectPayload) {
  const confirmPopup = popupManager.confirm.open({
    data: {
      title: t('payments.cpa_conversions.move_to_hold.confirm_title'),
      message: t('payments.cpa_conversions.move_to_hold.confirm_message', { id: row.incrementing_id }),
      bodyIcon: 'money-bill-wave',
      confirmTitle: t('crm.buttons.confirm'),
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;

        try {
          const response = await api.postCpaConversionManualPayout(row.incrementing_id, {
            rule_cpa_index: payload.cpaRuleIndex,
            data: payload.data,
          });

          const { created, fail_reason } = response.data.payload;

          if (created) {
            fetchData();
            context.handleClose();
          } else {
            context.handleClose();

            if (fail_reason) {
              const failPopup = popupManager.confirm.open({
                data: {
                  title: t('payments.cpa_conversions.move_to_hold.fail_reason_title'),
                  message: fail_reason,
                  bodyIcon: 'exclamation-triangle',
                  bodyIconColor: 'var(--text-negative)',
                  hideCancelButton: true,
                  confirmTitle: t('crm.buttons.close'),
                  confirmType: 'negative',
                },
                listeners: {
                  success: async () => {
                    const failContext = await failPopup;
                    failContext.handleClose();
                  },
                },
              });
            }
          }
        } catch (e) {
          context.handleClose();
        }
      },
    },
  });
}

function getCpaPermissionsFilter(data: UiTableNewFilter['filter']) {
  filterParams.value = data;
  handleRefresh();
}

async function getAffiliates(searchValue: string | undefined, ids: AffiliateId) {
  isSelectLoading.value = true;
  let query = {
    search: searchValue || undefined,
    id: ids || undefined,
    limit: !ids ? 100 : undefined,
    sort_column: 'email',
    sort_dir: 'asc',
  } as GetAffiliatesData['query'];

  try {
    const response = await api.getAffiliates(query);
    affiliatesList.value = response.data.payload;
  } catch (error) {
    console.error(error);
  } finally {
    isSelectLoading.value = false;
  }
}

async function getAffiliateOffers(id: string) {
  const queryParams = {
    sort_column: 'incrementing_id',
    sort_dir: 'asc',
  } as GetAffiliateOffersData['query'];

  try {
    const response = await api.getAffiliateOffers(id, queryParams);
    offersList.value = response.data.payload;
  } catch (error) {
    console.error(error);
  }
}
//#endregion

//#region watch
watch(() => notApplyFilterState.value.affiliate_id, (val) => {
  if (val) {
    const cleanId = Array.isArray(val) ? val[0] : val;
    if (typeof cleanId === 'string' && cleanId) {
      getAffiliateOffers(cleanId);
    }
  } else {
    delete notApplyFilterState.value.affiliate_offer_id;
  }
});
//#endregion

//#region hooks
initData();
//#endregion
</script>

<template>
  <div class="cpa-conversions">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('payments.page_title.cpa_conversions') }}
      </template>
      <template v-slot:left-slot>
        <DefaultFilter
          :model-value="filterParams"
          :filters-list="filtersList"
          @submit="getCpaPermissionsFilter"
          @open="initFilter"
          @not-apply-state="(state: UiTableNewFilter['filter']) => notApplyFilterState = state"
        />
      </template>
      <template v-slot:right-slot>
        <UiPagination
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      lazy-loading
      is-static
      i18n-path="payments.cpa_conversions"
      @sort="handleSort"
    >
      <template v-slot:append>
        <ElTableColumn
          align="center"
          width="76"
        >
          <template v-slot:header="scope">
            <div :key="scope.$index">
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>
          <template v-slot="scope">
            <ActionIcon
              icon="eye"
              :tooltip="$t('crm.buttons.view')"
              :disabled="!scope.row.current_state"
              @click="handleView(scope.row)"
            />
            <ActionIcon
              icon="money-bill-wave"
              color="var(--palette-status-info)"
              :tooltip="$t('payments.cpa_conversions.move_to_hold.action_tooltip')"
              :disabled="!canMoveToHold(scope.row)"
              @click="handleMoveToHold(scope.row)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>
